import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";

import ModalDragStrip from "./ModalDragStrip";

/** Where the session lives, and so where the deletion removes it from. */
export type RecordingLocation = "local" | "synced";

type RecordingDeleteDialogProps = {
  open: boolean;
  busy?: boolean;
  error?: string | null;
  /** Left out, the message doesn't say where the session is removed from. */
  location?: RecordingLocation;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
};

const RecordingDeleteDialog = ({
  open,
  busy = false,
  error = null,
  location,
  onCancel,
  onConfirm,
}: RecordingDeleteDialogProps) => {
  const { t } = useTranslation();
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) {
      setDeleteAcknowledged(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const confirmDisabled = busy || !deleteAcknowledged;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen && !busy) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-black/55">
          <ModalDragStrip />
        </Dialog.Overlay>
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[110] w-[calc(100%_-_2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-outline bg-surface p-5 text-text shadow-2xl"
          onOpenAutoFocus={() => {
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={event => {
            event.preventDefault();
            if (returnFocusRef.current?.isConnected)
              returnFocusRef.current.focus({ preventScroll: true });
          }}
          onEscapeKeyDown={event => {
            if (busy) event.preventDefault();
          }}
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger">
              <Trash2 size={20} />
            </div>
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-text">
                {t("delete_recording_title")}
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-sm leading-6 text-text-mid">
                {t("delete_recording_message", { context: location })}
              </Dialog.Description>
            </div>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm leading-5 text-text-mid">
            <input
              type="checkbox"
              checked={deleteAcknowledged}
              onChange={event => setDeleteAcknowledged(event.target.checked)}
              className="h-4 w-4 shrink-0 rounded border-outline accent-danger"
            />
            <span>{t("acknowledge_delete_recording")}</span>
          </label>

          {error && <p className="mt-4 text-sm text-danger">{error}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onCancel}
              className="rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text transition hover:bg-muted-surface disabled:opacity-60"
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              disabled={confirmDisabled}
              onClick={() => onConfirm()}
              className="rounded-lg border border-danger bg-danger px-3 py-2 text-sm font-medium text-white transition hover:bg-danger-strong disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? t("loading_ellipsis") : t("confirm_delete_recording")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RecordingDeleteDialog;
